import { useEffect, useRef, useState } from "react";
import { Camera, Images, X, Loader2, CheckCircle2, Lock, Clock } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/supabase";
import { compressImage, fmtBytes } from "../lib/media";
import { enqueueCompletion } from "../lib/contractorOffline";

const MAX = 3;

export const uploadCompletion = async (token, photos, note, secretHash) => {
  const fd = new FormData();
  photos.forEach((b, i) => fd.append("photos", b, `done-${i}.jpg`));
  if (note?.trim()) fd.append("note", note.trim().slice(0, 500));
  if (secretHash) fd.append("secret_hash", secretHash);
  let r;
  try { r = await fetch(`${API}/public/dispatch/${token}/completion`, { method: "POST", body: fd }); }
  catch {
    // offline / no signal: keep the compressed blobs in IDB and sync later; the UI shows the locked state now
    await enqueueCompletion(token, photos, note?.trim().slice(0, 500) || null, secretHash);
    return { queued: true, completion_photo_urls: photos.map((b) => URL.createObjectURL(b)), completion_note: note?.trim() || null, completion_locked_at: new Date().toISOString() };
  }
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(d.error || "upload_failed"), { status: r.status, claimed_by_name: d.claimed_by_name });
  return d;
};

const useProofPicker = (existing) => {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const left = MAX - existing - photos.length;
  const add = async (files) => {
    const picked = Array.from(files || []).slice(0, Math.max(0, left));
    setBusy(true);
    const out = [];
    for (const f of picked) {
      try { const r = await compressImage(f); out.push({ blob: r.blob, url: URL.createObjectURL(r.blob), before: r.before, after: r.after }); }
      catch { toast.error("Couldn't process that photo (unsupported format)"); }
    }
    setBusy(false);
    setPhotos((p) => [...p, ...out]);
  };
  const remove = (i) => setPhotos((p) => p.filter((_, j) => j !== i));
  return { photos, add, remove, left, busy, reset: () => setPhotos([]) };
};

const PhotoRow = ({ picker, t, testid }) => {
  const input = useRef(null);
  const gallery = useRef(null);
  return (
    <div className="flex gap-2 overflow-x-auto mt-3" data-testid={`${testid}-row`}>
      {picker.photos.map((p, i) => <div key={p.url} className="thumb"><img src={p.url} alt="" /><span className="thumb-size notranslate" translate="no" data-testid={`${testid}-size-${i}`}>{fmtBytes(p.before)} → {fmtBytes(p.after)}</span><button type="button" className="thumb-x" onClick={() => picker.remove(i)} aria-label="Remove" data-testid={`${testid}-remove-${i}`}><X size={12} /></button></div>)}
      {picker.busy && <div className="thumb thumb-add" data-testid={`${testid}-compressing`}><Loader2 size={20} className="animate-spin text-primary" /><span className="text-[10px]">…</span></div>}
      {picker.left > 0 && (<>
        <button type="button" className="thumb thumb-add" onClick={() => input.current?.click()} data-testid={`${testid}-add`}><Camera size={20} /><span className="text-[10px] leading-tight">{t.takePhoto}</span></button>
        <button type="button" className="thumb thumb-add" onClick={() => gallery.current?.click()} data-testid={`${testid}-gallery`}><Images size={20} /><span className="text-[10px] leading-tight">{t.chooseFromDevice}</span></button>
      </>)}
      <input ref={input} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { picker.add(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
      <input ref={gallery} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { picker.add(e.target.files); e.target.value = ""; }} data-testid={`${testid}-gallery-input`} />
    </div>
  );
};

export const CompletionSheet = ({ token, job, t, onDone, onSkip, secretHash }) => {
  const picker = useProofPicker(job.completion_photo_urls?.length || 0);
  const [note, setNote] = useState(job.completion_note || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!picker.photos.length && !note.trim()) return onSkip();
    setBusy(true);
    try { const d = await uploadCompletion(token, picker.photos.map((p) => p.blob), note, secretHash); toast.success(d.queued ? t.willSync : t.proofSent); onDone(d.queued ? { ...job, ...d, queued: true } : d); }
    catch (e) {
      const taken = e.status === 403;
      toast.error(e.message === "completion_locked" ? t.proofLocked : taken ? (e.message === "not_claimed" ? t.noticeNotClaimed : t.noticeTakenBy.replace("{name}", e.claimed_by_name || "—")) : e.status === 409 ? t.proofFull : e.status === 413 ? t.proofTooLarge : t.proofFailed);
      if (e.message === "completion_locked" || taken) onDone(null);
    }
    setBusy(false);
  };
  return (
    <div className="sheet-backdrop" onClick={onSkip} data-testid="completion-sheet-backdrop">
      <div className="sheet" onClick={(e) => e.stopPropagation()} data-testid="completion-sheet">
        <div className="flex items-center gap-2"><CheckCircle2 className="text-[#2ECC71]" size={22} /><p className="font-bold text-lg">{t.donePhotoTitle}</p></div>
        <p className="text-sm text-muted mt-1">{t.donePhotoBody}</p>
        {job.expires_at && <p className="text-xs text-muted mt-2 flex items-center gap-1.5" data-testid="completion-keep-report"><Clock size={12} /> {t.keepReportShort.split("{date}")[0]}<span className="notranslate" translate="no">{new Date(job.expires_at).toLocaleDateString()}</span>{t.keepReportShort.split("{date}")[1]}</p>}
        <PhotoRow picker={picker} t={t} testid="completion-photo" />
        <textarea className="field mt-3 min-h-[70px]" maxLength={500} placeholder={t.notePlaceholder} value={note} onChange={(e) => setNote(e.target.value)} data-testid="completion-note-input" />
        <div className="flex gap-3 mt-4">
          <button type="button" className="btn-secondary" onClick={onSkip} disabled={busy} data-testid="completion-skip">{t.skip}</button>
          <button type="button" className="btn-primary flex items-center justify-center gap-2" onClick={send} disabled={busy} data-testid="completion-send">{busy ? <><Loader2 className="animate-spin" size={16} /> {t.sending}</> : t.send}</button>
        </div>
      </div>
    </div>
  );
};

const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
const fmtCountdown = (ms) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

/** Lock = explicit completion_locked_at, or completed ≥ 1 h ago with nothing submitted (window closed). */
export const proofLock = (job, now = Date.now()) => {
  if (job.completion_locked_at) return { locked: true, at: job.completion_locked_at };
  const hasProof = (job.completion_photo_urls || []).length || job.completion_note;
  if (job.status === "completed" && job.completed_at && hasProof) return { locked: true, at: job.completed_at };
  if (job.status === "completed" && job.completed_at && !hasProof) {
    const closes = job.completion_window_closes_at || new Date(new Date(job.completed_at).getTime() + 3600000).toISOString();
    return now >= new Date(closes).getTime() ? { locked: true, at: closes } : { locked: false, closesAt: closes };
  }
  return { locked: false };
};

export const ProofCard = ({ token, job, t, onUpdate, onOpen, onLocked, secretHash, canEdit = true }) => {
  const existing = job.completion_photo_urls || [];
  const [adding, setAdding] = useState(false);
  const [now, setNow] = useState(Date.now());
  const lock = proofLock(job, now);
  useEffect(() => { if (lock.locked || !lock.closesAt) return; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [lock.locked, lock.closesAt]);
  if (!["viewed", "completed"].includes(job.status)) return null;
  if (!existing.length && !job.completion_note && job.status !== "completed") return null;
  const hasProof = existing.length > 0 || !!job.completion_note;
  return (
    <section className="card mt-5 fade-up" data-testid="proof-card" data-locked={lock.locked ? "true" : undefined}>
      <div className="flex items-center justify-between"><p className="eyebrow">{t.proofTitle}</p>{!lock.locked && canEdit && existing.length < MAX && <button type="button" className="balance-pill flex items-center gap-1" onClick={() => setAdding(true)} data-testid="proof-add-button"><Camera size={14} /> {t.addPhoto}</button>}</div>
      {lock.locked && <p className="text-xs text-muted mt-1 flex items-center gap-1" data-testid="proof-locked"><Lock size={12} /> {(hasProof ? t.proofLockedAt : t.proofWindowClosed).replace("{time}", fmtTime(lock.at))}</p>}
      {!lock.locked && lock.closesAt && <p className="text-xs text-muted mt-1" data-testid="proof-window"><span className="notranslate" translate="no">{t.proofWindow.replace("{time}", fmtCountdown(new Date(lock.closesAt).getTime() - now))}</span></p>}
      {existing.length > 0 && <div className="photo-grid mt-3" data-testid="proof-thumbs">{existing.map((u, i) => <button type="button" key={u} className="photo-tile" onClick={() => onOpen(u)} data-testid={`proof-photo-${i}`}><img src={u} alt="" loading="lazy" /></button>)}</div>}
      {job.completion_note && <p className="text-sm mt-3 result" data-testid="proof-note">“{job.completion_note}”</p>}
      {!existing.length && !job.completion_note && !lock.locked && <p className="text-sm text-muted mt-2" data-testid="proof-empty">{t.proofEmpty}</p>}
      {adding && <CompletionSheet token={token} job={job} t={t} secretHash={secretHash} onDone={(d) => { if (d) onUpdate(d); else onLocked?.(); setAdding(false); }} onSkip={() => setAdding(false)} />}
    </section>
  );
};
